import { post, postPage, searchPage, type PagedResult } from '@/lib/apiClient';
import { schoolScope } from './schoolScope';

export type ListParams = Record<string, unknown>;
export type PageFetcher<T> = (schoolId: number, params: ListParams, signal?: AbortSignal) => Promise<PagedResult<T>>;

/**
 * A single page from a named list route (e.g. `GetApplicantsPage`) that acts for the request's branch
 * (no school scope header). Read by POST: every param goes in the body.
 */
export function branchPagedList<Row>(url: string): PageFetcher<Row> {
  return (_schoolId, params, signal) => postPage<Row>(url, params, { signal });
}

/** {@link pagedList} for a list read at its own route (already `.../search`), not the collection's. */
export function postPagedList<Dto, Row = Dto>(url: string, map?: (dto: Dto) => Row): PageFetcher<Row> {
  return async (schoolId, params, signal) => {
    const result = await postPage<Dto>(url, params, { ...await schoolScope(schoolId), signal });
    return { ...result, items: map ? result.items.map(map) : result.items as unknown as Row[] };
  };
}

/**
 * A single page of a list read by POST: `bodyKeys` (the report's own query) go in the body, as for the
 * full list; the page, search, sorting and filters in the query string.
 */
export function postedPagedList<Row>(url: string, bodyKeys: readonly string[]): PageFetcher<Row> {
  return (_schoolId, params, signal) => {
    const body = Object.fromEntries(Object.entries(params).filter(([key]) => bodyKeys.includes(key)));
    const query = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => !bodyKeys.includes(key) && value !== undefined && value !== ''),
    );
    return post<PagedResult<Row>>(url, body, { params: query, signal });
  };
}

/**
 * A single page of a collection (`POST {collection}/search`) for tables; full-list APIs remain
 * available for form lookups.
 */
export function pagedList<Dto, Row = Dto>(collection: string, map?: (dto: Dto) => Row): PageFetcher<Row> {
  return async (schoolId, params, signal) => {
    const result = await searchPage<Dto>(collection, params, { ...await schoolScope(schoolId), signal });
    return { ...result, items: map ? result.items.map(map) : result.items as unknown as Row[] };
  };
}
